/**
 * Credit Memo Narratives — typed sections with inline citations.
 *
 * Replaces the legacy untyped `Record<string, string>` narratives blob on
 * `CreditMemoDto`. The new shape supports:
 *
 *   - Ordered, numbered sections rendered as `MemoSection` components
 *   - Markdown-ish paragraphs that interleave free text with
 *     `[[citation:<id>]]` inline tokens (the renderer swaps these for
 *     `CitationChip` components in the body prose)
 *   - Reasoning callouts (tinted blockquotes attributed to Astor) that
 *     carry their own footer citations
 *   - A global citation index so the right-rail "Citations" panel has a
 *     stable, ordered list independent of inline ordering
 *
 * Wave 1 — DTO contract only. Wave 2 (29-case-management-service) lands
 * the gRPC + Prisma side; Wave 3 (10-business-portal `CreditMemoTab`)
 * renders against this shape. Backend code that previously read narrative
 * keys as `string` (the old `Record<NarrativeSection, string>` shape)
 * MUST migrate to the new section/paragraph structure before consuming
 * this DTO.
 */

import { z } from 'zod';

// ============================================================================
// Citation
// ============================================================================

/**
 * Where a citation comes from. Drives the icon + tint on `CitationChip` and
 * tells the right-rail panel which "Sources" group to render under.
 *
 * - `POLICY_DOC`     — policy_analysis chip → policy doc + section ref
 * - `BANK_STATEMENT` — bank_statements:p3 chip → uploaded bank stmt + page
 * - `CREDIT_PULL`    — credit_pull chip → credit bureau pull
 * - `COMPS_REPORT`   — comps_auto_pull chip → comps doc
 * - `PROPERTY_REPORT`— strict_avm chip → AVM / appraisal
 * - `TWIN_INSIGHT`   — twin-generated reasoning (digital twin output)
 * - `TOOL_OUTPUT`    — generic tool-run output (catch-all for non-canonical sources)
 * - `MANUAL_NOTE`    — underwriter-added (typed in the editor, not auto-pulled)
 */
export const CreditMemoCitationSourceTypeSchema = z.enum([
  'POLICY_DOC',
  'BANK_STATEMENT',
  'CREDIT_PULL',
  'COMPS_REPORT',
  'PROPERTY_REPORT',
  'TWIN_INSIGHT',
  'TOOL_OUTPUT',
  'MANUAL_NOTE',
]);
export type CreditMemoCitationSourceType = z.infer<typeof CreditMemoCitationSourceTypeSchema>;

/**
 * A single citation referenced inline in body prose. `id` is a UUID stable
 * across narrative regenerations so the right-rail panel can keep its
 * scroll position when Astor updates the memo.
 */
export const CreditMemoCitationSchema = z.object({
  id: z.string().uuid(),
  /**
   * Short key shown on the chip (e.g. `policy_analysis`, `bank_statements:p3`).
   * Snake-case + colons; mono-rendered in the UI. Max 40 chars.
   */
  key: z.string().min(1).max(40),
  sourceType: CreditMemoCitationSourceTypeSchema,
  /**
   * Human label for hover/tooltip (e.g. `TILA §1024 — late-fee disclosure`).
   * Max 200 chars.
   */
  sourceLabel: z.string().min(1).max(200),
  /**
   * Stable ref the consumer can dereference (URL, doc-id, tool-run-id).
   * Optional — manual notes have no dereferenceable source.
   */
  sourceRef: z.string().optional(),
  /**
   * Confidence the citation actually supports the claim it's attached to.
   * 0-1. Renders as a small confidence indicator on the chip.
   */
  confidence: z.number().min(0).max(1).optional(),
});
export type CreditMemoCitationDto = z.infer<typeof CreditMemoCitationSchema>;

// ============================================================================
// Reasoning callout
// ============================================================================

/**
 * A reasoning paragraph attributed to Astor, rendered inline in a tinted
 * blockquote. Citations on a callout appear as a row of `InlineTagChip`s
 * in the callout footer (distinct from inline `CitationChip`s in body prose).
 */
export const ReasoningCalloutSchema = z.object({
  id: z.string().uuid(),
  /** The reasoning prose — typically one paragraph. */
  body: z.string().min(1),
  /** Citations that justify the reasoning. */
  citations: z.array(CreditMemoCitationSchema).default([]),
});
export type ReasoningCalloutDto = z.infer<typeof ReasoningCalloutSchema>;

// ============================================================================
// Section / paragraph
// ============================================================================

/**
 * A paragraph inside a `MemoSection`. Two kinds:
 *
 *   - `p`       — Markdown-ish text. Inline citation references appear as
 *                 `[[citation:<id>]]` tokens that the renderer swaps for
 *                 `CitationChip` components. The `<id>` MUST appear in the
 *                 memo's `citationIndex`.
 *   - `callout` — A reasoning callout (tinted blockquote).
 */
export const MemoParagraphSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('p'),
    /**
     * Markdown-ish text with inline `[[citation:<id>]]` tokens. The
     * renderer is responsible for token substitution; this DTO does not
     * validate that referenced IDs resolve (see `citationIndex` on the
     * parent schema for the source of truth).
     */
    text: z.string(),
  }),
  z.object({
    kind: z.literal('callout'),
    callout: ReasoningCalloutSchema,
  }),
]);
export type MemoParagraphDto = z.infer<typeof MemoParagraphSchema>;

/**
 * A numbered section of the memo (e.g. "1. Borrower Summary"). `number` is
 * a string (not int) because lenders sometimes use composite numbering
 * like "2a" / "2b" for sub-recommendations.
 */
export const MemoSectionSchema = z.object({
  id: z.string().uuid(),
  /**
   * Section ordering label (e.g. `1`, `2`, `2a`). Rendered as the leading
   * number in the heading rule. Max 4 chars accommodates `12` / `2a` / `3b`.
   */
  number: z.string().min(1).max(4),
  title: z.string().min(1).max(200),
  body: z.object({
    paragraphs: z.array(MemoParagraphSchema),
  }),
});
export type MemoSectionDto = z.infer<typeof MemoSectionSchema>;

// ============================================================================
// Root — credit memo narratives blob
// ============================================================================

/**
 * The full typed narratives blob stored on `CreditMemoDto.narratives`.
 *
 * Invariants the renderer relies on (NOT enforced by Zod — backend service
 * is responsible for upholding them at write time):
 *
 *   1. Every `[[citation:<id>]]` token inside a `p` paragraph or a callout
 *      body MUST resolve to a citation in `citationIndex`.
 *   2. Citation IDs in `citationIndex` are unique within a memo.
 *   3. Section `number` values are unique within a memo and follow human
 *      reading order (the consumer renders sections in array order, not
 *      by sorting on `number`).
 */
export const CreditMemoNarrativesSchema = z.object({
  /**
   * Authorship attribution shown in the memo header (e.g. "Generated by
   * Astor"). Defaults to `'astor'` — manual underwriter-authored memos
   * pass the user's display name here.
   */
  generatedBy: z.string().default('astor'),
  /** ISO timestamp of last generation. Powers "regenerated 2 minutes ago". */
  generatedAt: z.string().datetime().optional(),
  /** Total tool calls Astor made while drafting (header chip). */
  toolCallCount: z.number().int().min(0).default(0),
  /** Total policy checks Astor ran while drafting (header chip). */
  policyCheckCount: z.number().int().min(0).default(0),
  /** Section sequence — at least one section is required. */
  sections: z.array(MemoSectionSchema).min(1),
  /**
   * Global citation index for the right-rail "Citations" panel. Every
   * citation referenced by an inline `[[citation:<id>]]` token MUST be
   * listed here; orphan inline tokens are a service-layer error.
   */
  citationIndex: z.array(CreditMemoCitationSchema).default([]),
});
export type CreditMemoNarrativesDto = z.infer<typeof CreditMemoNarrativesSchema>;
